<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref, watch, onBeforeMount } from "vue";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import MaterialBasicsSection from "./MaterialBasicsSection.vue";
import CheckBox from "@/components/Reusables/Inputs/CheckBox.vue";
import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import { formatNumber } from "@/services/Utils/DecimalFormatter";
import { useEnum } from "@/stores/project/project.enum";
import { Country_Enum, useLocalization } from "@/providers/Localization";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";

const enumStore = useEnum();
const { declaredUnits, getMassUnit } = enumStore;

const localizationStore = useLocalization();
const { country } = storeToRefs(localizationStore);
const props = defineProps<{
  material: SpeckleMaterial;
  materialCount: number;
  index: number;
  library: any;
}>();

const emits = defineEmits<{
  (e: "isMaterialValid", value: boolean, index: number): void;
  (event: "updateLifetime", payload: number): void;
  (event: "toggle-lifetime-table"): void;
}>();

// The form's save is unavailable to a Viewer (RTLCA-5582) and while a frozen revision is previewed
// (RTLCA-5253) — CompleteMappingView guards it — so the fields it feeds go inert with it rather
// than inviting edits that can never be committed. `useProjectEditReadOnly` folds both reasons into
// the one signal the inventory tree threads down as `userCanEdit`, and fails closed while the
// project's permissions are still loading.
const { isReadOnly, readOnlyTooltip } = useProjectEditReadOnly("top");

const canEdit = computed(() => !isReadOnly.value);

const applyLifetimeToAll = ref<boolean>(false);

// Getter and setter for unit newValue
const getUnitNewValue = (unit: any) => {
  return computed({
    get: () => unit.newValue ?? null,
    set: (value: number | null) => {
      unit.newValue = value;
    },
  });
};

const getMassUnitItem = computed(() => getMassUnit(props.library.declaredUnit.massUnit));
const addNewUnit = ref(false);
const showUnitTable = ref(true);

onBeforeMount(() => {
  const material = props.material;

  if (props.library.expectedLifespan != null) {
    material.lifeTime = props.library.expectedLifespan;
  }

  // Ensure totalDeclaredUnits is initialised — the API may return null for newly-created materials
  if (!material.totalDeclaredUnits) {
    material.totalDeclaredUnits = [];
  }

  const libraryDeclaredUnit = props.library.declaredUnit?.declaredUnit;

  // Find existing unit with matching declaredUnit
  const existingUnit = material.totalDeclaredUnits?.find((unit: any) => unit.declaredUnit === libraryDeclaredUnit);

  if (existingUnit) {
    // Always ensure the library's declared unit is preferred
    material.totalDeclaredUnits?.forEach((unit: any) => {
      unit.preferred = unit.declaredUnit === libraryDeclaredUnit;
    });
  } else if (getMassUnitItem.value) {
    const existingMassUnit = material.totalDeclaredUnits?.find(
      (unit: any) => unit.declaredUnit === getMassUnitItem.value.declaredUnit
    );
    if (existingMassUnit) {
      material.totalDeclaredUnits?.forEach((unit: any) => {
        unit.preferred = unit.declaredUnit === getMassUnitItem.value.declaredUnit;
      });
    } else {
      addNewUnit.value = true;
      material.totalDeclaredUnits?.forEach((unit: any) => {
        unit.preferred = false;
      });
      const newUnit: any = {
        declaredUnit: props.library.declaredUnit.declaredUnit,
        preferred: true,
        value: 0,
        newValue: null,
        source: 0,
        wasChanged: false,
        isNew: true,
      };
      material.totalDeclaredUnits.push(newUnit);
    }
  }

  showUnitTable.value = !materialValidUnit.value;
});

const materialValidUnit = computed(() => {
  const material = props.material;
  const units = material.totalDeclaredUnits ?? [];
  const libraryDeclaredUnit = props.library.declaredUnit?.declaredUnit;

  // Helper: does a unit have a positive value?
  const unitHasValue = (u: any | undefined) => {
    if (!u) return false;
    const v = u.newValue ?? u.value;
    return v != null && Number(v) > 0;
  };

  // Unit matching the library's declared unit (we don't care if it's new or not here)
  const matchingUnit = units.find((u: any) => u.declaredUnit === libraryDeclaredUnit);

  // Preferred unit (if any)
  const preferredUnit = units.find((u: any) => u.preferred);

  // ✅ valid if either matchingUnit OR preferredUnit has a positive value
  return unitHasValue(matchingUnit) || unitHasValue(preferredUnit);
});

const materialValidityMap = computed(() => {
  const material = props.material;

  // ✅ valid if either matchingUnit OR preferredUnit has a positive value
  const hasValidUnitValue = materialValidUnit.value;

  // Lifetime logic
  let lifetimeValue = material.lifeTime ?? material.mapping?.expectedLifespan;
  // You can simplify this since cloneMaterial/currentMaterialIndex are gone
  if (applyLifetimeToAll.value && material.lifeTime != null) {
    lifetimeValue = material.lifeTime;
  }

  const hasValidLifetime = lifetimeValue != null && Number(lifetimeValue) > 0;

  const isValid = hasValidLifetime && hasValidUnitValue;

  return isValid;
});

const showMaterialBasicsSection = computed(() => {
  return country.value !== Country_Enum.NORWAY;
});

const updateLifetime = (val: number) => {
  emits("updateLifetime", val);
};
watch(
  materialValidityMap,
  (isValid) => {
    emits("isMaterialValid", isValid, props.index);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col">
    <div class="rounded-4 mb-6 flex gap-6 border px-3 pb-3.5">
      <slot name="indicators"></slot>
      <div class="flex w-full flex-col">
        <div
          class="sticky z-2 bg-slate-100 pt-3 pr-2 pb-2"
          style="top: -36px">
          <!-- style kept: negative 36px offset not expressible with standard Tailwind spacing tokens -->
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <p class="t-sh4 pb-1">{{ material.identifier }}</p>
            </div>
          </div>
        </div>

        <MaterialBasicsSection
          v-if="showMaterialBasicsSection"
          :material="material"
          :can-edit="canEdit"
          @update-lifetime="updateLifetime">
          <div class="text-type-muted flex w-fit flex-1 flex-col items-baseline gap-2 text-sm font-bold">
            <span class="shrink-0 text-xs">Lifetime (years)</span>

            <NumberInput
              :value="material.lifeTime"
              color="primary"
              class="w-32 grow-0"
              no-decimals
              positive
              :disabled="!canEdit"
              :error-msg="material.lifeTime === 0 ? 'Missing lifetime' : ''"
              placeholder="Custom lifetime"
              :max="300"
              @update:value="(val: any) => updateLifetime(Number(val))" />

            <RevisionGatedButton
              label="Lifetime table"
              color="button"
              class="w-full shrink-0"
              :guard="useProjectEditReadOnly"
              @click="() => canEdit && emits('toggle-lifetime-table')" />
          </div>
        </MaterialBasicsSection>
        <div
          v-else
          class="text-type-muted flex w-full flex-1 flex-col items-baseline gap-2 text-sm font-bold">
          <span class="shrink-0 text-xs">Lifetime (years)</span>

          <div class="flex w-full grow gap-2">
            <NumberInput
              :value="material.lifeTime"
              color="primary"
              no-decimals
              positive
              :disabled="!canEdit"
              :error-msg="material.lifeTime === 0 ? 'Missing lifetime' : ''"
              placeholder="Custom lifetime"
              :max="300"
              @update:value="(val: any) => updateLifetime(Number(val))" />
            <RevisionGatedButton
              label="Lifetime table"
              color="button"
              class="h-[40px] w-full max-w-36 shrink-0"
              :guard="useProjectEditReadOnly"
              @click="() => canEdit && emits('toggle-lifetime-table')" />
          </div>
        </div>

        <div
          v-if="showUnitTable"
          class="text-text-color min-w-[500px] pt-4 text-sm">
          <div class="border-element-stroke rounded-16 grid grid-cols-12 rounded-b-none border bg-slate-100 p-2 font-semibold">
            <span class="col-span-2 pl-2">Preferred</span>
            <span class="col-span-3">Unit</span>
            <span class="col-span-3">Value</span>
            <span class="col-span-4"> Assign new value </span>
          </div>

          <div
            v-for="unit in material.totalDeclaredUnits"
            :key="unit.declaredUnit"
            class="border-element-stroke text-type-muted rounded-16 rounded-t-none rounded-b-none border-x border-b last:rounded-b">
            <div
              v-if="unit.preferred"
              class="grid grid-cols-12 items-center px-4 py-2">
              <span class="col-span-2">
                <CheckBox
                  custom-class="rounded-full!"
                  color="input"
                  size="lg"
                  :disabled="!canEdit"
                  :value="unit.preferred" />
              </span>
              <span class="col-span-3">
                {{ declaredUnits[unit.declaredUnit].DisplayName }}
              </span>
              <span class="col-span-3">{{ formatNumber(unit.value ?? 0) }}</span>

              <div
                v-tooltip="readOnlyTooltip"
                class="col-span-4">
                <NumberInput
                  color="primary"
                  placeholder="new value"
                  :value="getUnitNewValue(unit).value"
                  :max-decimals="3"
                  :disabled="!canEdit"
                  :error-msg="unit.value === 0 && (unit.newValue === 0 || unit.newValue == undefined) ? 'Missing value' : ''"
                  class="w-4/5"
                  @update:value="getUnitNewValue(unit).value = $event" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
